import { useMemo, useState } from 'react';
import { format } from 'date-fns';

import {
  CompletionChart,
  CostStats,
  DurationStats,
  EndpointDurationChart,
  EndpointSuccessChart,
  ErrorAnalysis,
} from '../../components/stats';
import type { DateRange } from '../../types';
import {
  type DateRangePreset,
  getDateRangeFromPreset,
  useVideoStats,
} from '../hooks/useVideoStats';

/**
 * Video Statistics page — visually identical to lightning's
 * `web/src/pages/StatsPage.tsx`, with the same six panels:
 *
 *  Row 1: Duration Analysis, Cost Analysis, Endpoint Success Rate
 *  Row 2: Completion Rate, Error Analysis, Endpoint Avg Duration
 *
 * The lightning page also has a "by version" toggle and a pricing
 * settings layer that turns FAL token counts into dollar amounts. The
 * video service doesn't need either: it always runs a single FAL
 * pipeline per scan, and the dollar cost comes back directly in each
 * FAL response's `usage.cost` field, so we just sum it.
 */
export function VideoStatsPage() {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('last7days');
  const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });

  const dateRange = useMemo(() => {
    if (selectedPreset === 'custom') return customRange;
    return getDateRangeFromPreset(selectedPreset);
  }, [selectedPreset, customRange]);

  const { stats, loading, error } = useVideoStats(dateRange);

  return (
    <div className="flex flex-col gap-3">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-soft flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-surface-600">Range:</span>
            <div className="flex rounded border border-surface-200 overflow-hidden">
              {(['today', 'last7days', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => setSelectedPreset(preset)}
                  className={`px-3 py-1 text-xs font-medium transition-all duration-200 ${
                    selectedPreset === preset
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {preset === 'today' && 'Today'}
                  {preset === 'last7days' && '7 Days'}
                  {preset === 'last30days' && '30 Days'}
                  {preset === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {selectedPreset === 'custom' && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={customRange.from ? format(customRange.from, 'yyyy-MM-dd') : ''}
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    from: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-xs">to</span>
              <input
                type="date"
                value={customRange.to ? format(customRange.to, 'yyyy-MM-dd') : ''}
                onChange={(e) =>
                  setCustomRange((prev) => ({
                    ...prev,
                    to: e.target.value ? new Date(e.target.value + 'T23:59:59') : null,
                  }))
                }
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}

          {stats && (
            <div className="ml-auto flex items-center gap-3 text-xs text-surface-500">
              <span>
                <strong className="text-surface-700">{stats.totalJobs}</strong> scans
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-3 p-2 rounded bg-danger-light border border-danger/30 text-danger-dark text-xs flex items-center gap-2 flex-shrink-0">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <svg className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-surface-500">Loading statistics...</p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && !loading && (
        <div className="grid grid-cols-1 gap-3 auto-rows-[minmax(24rem,auto)] sm:auto-rows-[minmax(22rem,auto)] lg:grid-cols-3">
          <DurationStats stats={stats.duration} title="Duration Analysis" />
          <CostStats stats={stats.cost} title="Cost Analysis" />
          <EndpointSuccessChart stats={stats.endpointStats} title="Endpoint Success Rate" />

          <CompletionChart stats={stats.completion} title="Completion Rate" />
          <ErrorAnalysis errors={stats.errors} title="Error Analysis" />
          <EndpointDurationChart stats={stats.endpointStats} title="Endpoint Avg Duration" />
        </div>
      )}

      {/* Empty / no-data states (same copy as lightning) */}
      {!loading && !error && !stats &&
        selectedPreset === 'custom' &&
        (!customRange.from || !customRange.to) && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-surface-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-surface-500 mb-2">Select a date range to view statistics</p>
              <p className="text-sm text-surface-400">Choose start and end dates above</p>
            </div>
          </div>
        )}

      {!loading && !error && stats && stats.totalJobs === 0 && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-surface-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-surface-500 mb-2">No scan data found for this period</p>
            <p className="text-sm text-surface-400">Try selecting a different date range</p>
          </div>
        </div>
      )}
    </div>
  );
}
